import { useState, useEffect, useRef } from "react"
import { Plus, Minus, Loader2 } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import Lottie from "react-lottie-player"
import type { ArchitectureStyle } from "./architecture-style-modal"

// Sample lottie data URL - this should be moved to assets directory in production
const LOTTIE_URL = "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/center-bubble-6U03KFaOIXIeipVvP2g2iQBwyFopVq.json";

interface BubbleStyle {
  id: string
  name: string
  image: string
  type?: "architecture" | "color" | "inspiration" | "keyword"
  architectureData?: ArchitectureStyle
  isAddButton?: boolean
}

interface BubbleVisualizerProps {
  centerBubble: BubbleStyle
  bubbleStyles: BubbleStyle[]
  onAddStyle: () => void
  onRemoveBubble: (id: string) => void
  isModalOpen: boolean
  isGenerating?: boolean
  onSelect?: (id: string) => void
}

export function BubbleVisualizer({
  centerBubble,
  bubbleStyles,
  onAddStyle,
  onRemoveBubble,
  isModalOpen,
  isGenerating = false,
  onSelect,
}: BubbleVisualizerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
  const [lottieData, setLottieData] = useState<any>(null)

  // Fetch the Lottie animation data
  useEffect(() => {
    fetch(LOTTIE_URL)
      .then((response) => response.json())
      .then((data) => {
        setLottieData(data)
      })
      .catch((error) => {
        console.error("Error loading Lottie animation:", error)
      })
  }, [])

  // Calculate container size on mount and resize
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setContainerSize({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        })
      }
    }

    updateSize()
    window.addEventListener("resize", updateSize)
    return () => window.removeEventListener("resize", updateSize)
  }, [])

  // Get shadow color based on bubble type
  const getBubbleShadow = (type?: string) => {
    switch (type) {
      case "architecture":
        return "shadow-[0_0_15px_rgba(109,76,94,0.5)]"
      case "color":
        return "shadow-[0_0_15px_rgba(59,130,246,0.5)]"
      case "inspiration":
        return "shadow-[0_0_15px_rgba(245,158,11,0.5)]"
      case "keyword":
        return "shadow-[0_0_15px_rgba(34,197,94,0.5)]"
      default:
        return "shadow-md"
    }
  }

  // Calculate positions for surrounding bubbles
  const getSurroundingBubbles = () => {
    // Create a copy of bubbleStyles and add the "Add Style" bubble
    const allBubbles = [
      ...bubbleStyles,
      {
        id: "add-style",
        name: "Add Inspiration",
        image: "",
        isAddButton: true,
      },
    ]

    return allBubbles.map((bubble, index) => {
      const angle = (index / allBubbles.length) * 2 * Math.PI
      const radius = Math.min(containerSize.width, containerSize.height) * 0.35

      return {
        ...bubble,
        angle,
        radius,
      }
    })
  }

  const surroundingBubbles = getSurroundingBubbles()

  // Determine animation speed based on generating state
  const animationSpeed = isGenerating ? 2.5 : 1

  return (
    <div
      ref={containerRef}
      className={`bg-gray-100 h-[500px] relative rounded-tr-md rounded-b-md overflow-hidden ${isModalOpen ? "filter blur-sm" : ""} p-52 `}
    >
      {/* Center Bubble with Lottie Animation */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="relative" style={{ width: 220, height: 220, bottom: 10 }}>
          {lottieData ? (
            <Lottie
              loop
              animationData={lottieData}
              play
              speed={animationSpeed} // Speed up during generation
              style={{ width: 220, height: 220 }}
              className="absolute inset-0"
            />
          ) : (
            <div className="w-[220px] h-[220px] rounded-full bg-dzinly-primary/20 animate-pulse"></div>
          )}

          {/* Center image overlaid on the Lottie animation */}
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{
              width: 100,
              height: 100,
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <img
              src={centerBubble.image || "/placeholder.svg"}
              alt={centerBubble.name}
              className="w-[100px] h-[100px] object-cover rounded-full"
              onError={(e) => {
                e.currentTarget.src = "/placeholder.svg?height=200&width=200"
              }}
            />
          </div>
        </div>

        {/* Loading spinner below center bubble */}
        {isGenerating && (
          <div className="absolute" style={{ top: "70%", left: "50%", transform: "translateX(-50%)" }}>
            <div className="flex flex-col items-center">
              <Loader2 className="h-8 w-8 animate-spin text-dzinly-primary" />
              <p className="mt-2 text-sm font-medium text-dzinly-primary bg-white px-3 py-1 rounded-md shadow-sm">
                Generating design...
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Surrounding Bubbles - Only show if not generating */}
      {!isGenerating && (
        <AnimatePresence>
          {surroundingBubbles.map((bubble, index) => {
            // Calculate position based on angle and radius
            const x = containerSize.width / 2 + bubble.radius * Math.cos(bubble.angle)
            const y = (containerSize.height / 2 - 20) + bubble.radius * Math.sin(bubble.angle)

            return (
              <motion.div
                key={bubble.id}
                className="absolute"
                style={{
                  width: 80,
                  height: "auto",
                  left: 0,
                  top: 0,
                  transform: `translate(${x - 40}px, ${y - 40}px)`,
                  zIndex: 5,
                }}
                initial={{
                  opacity: 0,
                  x: containerSize.width / 2 - 40,
                  y: containerSize.height / 2 - 40,
                  scale: 0.5,
                }}
                animate={{
                  opacity: 1,
                  x: x - 40,
                  y: y - 40,
                  scale: 1,
                  transition: {
                    type: "spring",
                    stiffness: 260,
                    damping: 20,
                    delay: index * 0.05,
                  },
                }}
                exit={{
                  opacity: 0,
                  x: containerSize.width / 2 - 40,
                  y: containerSize.height / 2 - 40,
                  scale: 0.5,
                  transition: { duration: 0.2 },
                }}
              >
                <div className="flex flex-col items-center">
                  {/* Bubble Circle */}
                  <motion.div
                    className="relative w-20 h-20 cursor-pointer"
                    onClick={bubble.isAddButton ? onAddStyle : undefined}
                    whileHover={{ scale: 1.1 }}
                    whileTap={bubble.isAddButton ? { scale: 0.95 } : undefined}
                  >
                    {bubble.isAddButton ? (
                      <div className="w-full h-full rounded-full overflow-hidden shadow-[0_0_15px_rgba(109,76,94,0.5)] bg-white flex items-center justify-center">
                        <Plus className="h-8 w-8 text-dzinly-primary" />
                      </div>
                    ) : (
                      <>
                        {bubble.type === "keyword" ? (
                          // Keyword bubble as rounded rectangle with text
                          <div
                            className={`w-full rounded-lg overflow-hidden flex items-center justify-center text-center p-3 bg-white ${getBubbleShadow(bubble.type)}`}
                            onClick={() => !bubble.isAddButton && onSelect && onSelect(bubble.id)}
                          >
                            <span className="text-sm font-medium text-gray-800">{bubble.name}</span>
                          </div>
                        ) : (
                          // Regular image bubble
                          <div
                            className={`w-full h-full rounded-full overflow-hidden ${getBubbleShadow(bubble.type)}`}
                            onClick={() => !bubble.isAddButton && onSelect && onSelect(bubble.id)}
                          >
                            <img
                              src={bubble.architectureData?.preview_image || bubble.image || "/placeholder.svg"}
                              alt={bubble.name}
                              className="w-full h-full object-cover rounded-full"
                              onError={(e) => {
                                // Bubble image failed to load
                                e.currentTarget.src = "/placeholder.svg?height=100&width=100"
                              }}
                            />
                          </div>
                        )}
                        <button
                          className="absolute -right-2 -top-2 bg-dzinly-primary text-white rounded-full p-1 z-20 shadow-md"
                          onClick={(e) => {
                            e.stopPropagation()
                            onRemoveBubble(bubble.id)
                          }}
                        >
                          <Minus size={14} />
                        </button>
                      </>
                    )}
                  </motion.div>

                  {/* Label below the bubble - don't show for keywords as they already have text */}
                  {(bubble.type !== "keyword" || bubble.isAddButton) && (
                    <div className="text-center mt-2 font-medium text-xs bg-white px-2 py-1 rounded-md shadow-sm">
                      {bubble.name}
                    </div>
                  )}
                </div>
              </motion.div>
            )
          })}
        </AnimatePresence>
      )}
    </div>
  )
}